// Workspace-wide throttle governing EVERY workflow — the volume/velocity guardrails configured
// from the Workflows main page. Three independent knobs, all optional (absent = off):
//   monthlyCap        — max outbound actions (SMS, calls, notifications) per calendar month (Eastern).
//   dailyCap          — same budget but per day; stops a runaway workflow inside a month.
//   enrollmentsPerHour — drip rate for processing enrollments, so "enroll 500" releases gradually.
//
// These persist as real Workspace fields (maxOutboundMonthly / maxOutboundDaily /
// maxEnrollmentsPerHour — TRK-5385, PR #406) and the engine enforces them: a run that hits a cap
// HOLDS (waiting/throttle_cap) and auto-resumes when the window resets — verified live 08-10.
//
// A cap of ZERO is a real value, not "off": the engine blocks every send and parks the runs (api
// #519, QA'd 08-20 — a run held at daily cap 0 with resumeAt midnight Eastern, then woke and sent 3
// seconds after the cap was raised). So it is a pause button that keeps the queue rather than
// dropping it. We used to sanitise 0 away as "unset", which meant someone typing 0 to stop all
// sending saved "no limit" and got the exact opposite. Absent still means off; zero means stop.

export type GlobalWorkflowSettings = {
  monthlyCap?: number
  dailyCap?: number
  enrollmentsPerHour?: number
}

/** The three throttle fields as they live on the Workspace document. The globally generated
 *  Workspace type predates them (regenerating it wholesale drags unrelated schema drift onto this
 *  branch), so this narrow view types just what this module reads. */
export type WorkspaceThrottleFields = {
  maxOutboundMonthly?: number | null
  maxOutboundDaily?: number | null
  maxEnrollmentsPerHour?: number | null
}

function positiveInt(value: unknown): number | undefined {
  const n = typeof value === `number` ? Math.floor(value) : NaN
  return Number.isFinite(n) && n >= 1 ? n : undefined
}

/** Caps accept zero, which means "send nothing" rather than "no cap". */
function capInt(value: unknown): number | undefined {
  const n = typeof value === `number` ? Math.floor(value) : NaN
  return Number.isFinite(n) && n >= 0 ? n : undefined
}

/** Coerce untrusted input (form state, remote doc) into a valid settings shape. Caps take integers
 *  ≥ 0 (zero pauses sending); the enrollment pace takes ≥ 1, since a pace of zero would stall
 *  enrolment with no way for the engine to release it. Anything else means that throttle is off.
 *
 *  Note the `!== undefined` checks: a truthy test would drop a cap of 0 and silently mean the
 *  opposite. That is the same bug the engine had until api #519, and it is easy to reintroduce. */
export function sanitizeGlobalSettings(raw: unknown): GlobalWorkflowSettings {
  if (!raw || typeof raw !== `object`) return {}
  const source = raw as Record<keyof GlobalWorkflowSettings, unknown>
  const settings: GlobalWorkflowSettings = {}
  const monthly = capInt(source.monthlyCap)
  if (monthly !== undefined) settings.monthlyCap = monthly
  const daily = capInt(source.dailyCap)
  if (daily !== undefined) settings.dailyCap = daily
  const pace = positiveInt(source.enrollmentsPerHour)
  if (pace !== undefined) settings.enrollmentsPerHour = pace
  return settings
}

/** Read the throttle contract off a fetched Workspace document. */
export function settingsFromWorkspace(workspace: WorkspaceThrottleFields | undefined): GlobalWorkflowSettings {
  return sanitizeGlobalSettings({
    monthlyCap: workspace?.maxOutboundMonthly ?? undefined,
    dailyCap: workspace?.maxOutboundDaily ?? undefined,
    enrollmentsPerHour: workspace?.maxEnrollmentsPerHour ?? undefined,
  })
}

/** UpdateWorkspaceInput payload for a settings shape. Explicit nulls clear a previously-set cap —
 *  omitting the field would leave the old limit silently in force. */
export function workspaceInputFromSettings(settings: GlobalWorkflowSettings): WorkspaceThrottleFields {
  const clean = sanitizeGlobalSettings(settings)
  return {
    maxOutboundMonthly: clean.monthlyCap ?? null,
    maxOutboundDaily: clean.dailyCap ?? null,
    maxEnrollmentsPerHour: clean.enrollmentsPerHour ?? null,
  }
}

/** Compact human summary for the list-page header pill (e.g. "500/mo · 200/day · 50/hr pace"). */
export function throttleSummary(settings: GlobalWorkflowSettings): string {
  const parts: string[] = []
  if (settings.monthlyCap != null) parts.push(`${settings.monthlyCap}/mo`)
  if (settings.dailyCap != null) parts.push(`${settings.dailyCap}/day`)
  if (settings.enrollmentsPerHour != null) parts.push(`${settings.enrollmentsPerHour}/hr pace`)
  return parts.join(` · `)
}
